/**
 * JourneyStepsSection (P16.3 / #485).
 *
 * Nested editor for journey_steps inside the Journey drawer.
 * Mirrors LinkSection's layout but with editable rows --
 * step_number + name + description per row, plus inline
 * "Add step" + per-row Save / Delete.
 *
 * The nested admin endpoints:
 *   GET    /v1/admin/foundation/journeys/{id}/steps
 *   POST   /v1/admin/foundation/journeys/{id}/steps
 *   PATCH  /v1/admin/foundation/journeys/{id}/steps/{step_id}
 *   DELETE /v1/admin/foundation/journeys/{id}/steps/{step_id}
 *
 * Reorder isn't shipped at v0.16.0 -- the admin edits step_number
 * directly as a number input. Drag-and-drop reorder is polish
 * (backlog from the P16 plan doc).
 */
import { type ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "shared/api/client";

interface Step {
  id: string;
  journey_id: string;
  step_number: number;
  name: string;
  description: string | null;
  // P28.2 (#540): entity tags so a step can declare the
  // underlying entity it depends on. The cascade walker uses
  // these to attribute the failing step when a tagged entity
  // has an open Finding.
  service_id: string | null;
  capability_id: string | null;
  vendor_id: string | null;
}

interface StepDraft {
  step_number: number;
  name: string;
  description: string | null;
  service_id: string | null;
  capability_id: string | null;
  vendor_id: string | null;
}

type TagKind = "service" | "capability" | "vendor";

interface TagOption {
  id: string;
  name: string;
  code: string;
}

const TAG_ENDPOINTS: Record<TagKind, string> = {
  service: "/v1/admin/foundation/services?limit=500",
  capability: "/v1/admin/foundation/capabilities?limit=500",
  vendor: "/v1/admin/foundation/vendors?limit=500",
};

export function JourneyStepsSection({ rowId }: { rowId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const queryKey = ["foundation-admin", "journeys", rowId, "steps"] as const;
  const listEndpoint = `/v1/admin/foundation/journeys/${rowId}/steps`;

  const steps = useQuery({
    queryKey,
    queryFn: () => api.get<Step[]>(listEndpoint),
    staleTime: 10_000,
  });

  const addMut = useMutation({
    mutationFn: (draft: StepDraft) => api.post<Step>(listEndpoint, draft),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });
  const patchMut = useMutation({
    mutationFn: ({ stepId, body }: { stepId: string; body: Partial<StepDraft> }) =>
      api.patch<Step>(`${listEndpoint}/${stepId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });
  const removeMut = useMutation({
    mutationFn: (stepId: string) => api.delete<void>(`${listEndpoint}/${stepId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });

  const nextStepNumber =
    (steps.data ?? []).reduce((m, s) => Math.max(m, s.step_number), 0) + 1;

  return (
    <section data-testid="journey-steps-section">
      <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
        {t("modules.foundation.admin.journeys.steps.heading")}
      </h3>

      {steps.data && steps.data.length === 0 && (
        <p data-testid="journey-steps-empty" className="text-xs text-slate-500 mb-2">
          {t("modules.foundation.admin.journeys.steps.empty")}
        </p>
      )}

      <ol className="space-y-2 mb-3">
        {(steps.data ?? []).map((step) => (
          <li key={step.id} data-testid="journey-step-row" data-step-id={step.id}>
            <StepEditor
              step={step}
              onSave={(body) => patchMut.mutate({ stepId: step.id, body })}
              onDelete={() => removeMut.mutate(step.id)}
              savingLabel={t("modules.foundation.admin.journeys.steps.saving")}
            />
          </li>
        ))}
      </ol>

      <NewStepRow
        nextStepNumber={nextStepNumber}
        onAdd={(draft) => addMut.mutate(draft)}
        adding={addMut.isPending}
      />
    </section>
  );
}

interface StepEditorProps {
  step: Step;
  onSave: (body: Partial<StepDraft>) => void;
  onDelete: () => void;
  savingLabel: string;
}

function StepEditor({ step, onSave, onDelete }: StepEditorProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<StepDraft>({
    step_number: step.step_number,
    name: step.name,
    description: step.description,
    service_id: step.service_id,
    capability_id: step.capability_id,
    vendor_id: step.vendor_id,
  });
  useEffect(() => {
    setDraft({
      step_number: step.step_number,
      name: step.name,
      description: step.description,
      service_id: step.service_id,
      capability_id: step.capability_id,
      vendor_id: step.vendor_id,
    });
  }, [
    step.id,
    step.step_number,
    step.name,
    step.description,
    step.service_id,
    step.capability_id,
    step.vendor_id,
  ]);

  const dirty =
    draft.step_number !== step.step_number ||
    draft.name !== step.name ||
    draft.description !== step.description ||
    draft.service_id !== step.service_id ||
    draft.capability_id !== step.capability_id ||
    draft.vendor_id !== step.vendor_id;

  return (
    <div className="rounded border border-slate-200 bg-white p-2 space-y-1">
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={1}
          value={draft.step_number}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setDraft((d) => ({ ...d, step_number: Number(e.target.value) }))
          }
          data-testid="journey-step-number"
          className="w-16 rounded border border-slate-300 px-1 py-0.5 text-sm tabular-nums"
        />
        <input
          type="text"
          value={draft.name}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setDraft((d) => ({ ...d, name: e.target.value }))
          }
          data-testid="journey-step-name"
          placeholder={t("modules.foundation.admin.journeys.steps.namePlaceholder")}
          className="flex-1 rounded border border-slate-300 px-2 py-0.5 text-sm"
        />
      </div>
      <textarea
        rows={2}
        value={draft.description ?? ""}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
          setDraft((d) => ({
            ...d,
            description: e.target.value === "" ? null : e.target.value,
          }))
        }
        data-testid="journey-step-description"
        placeholder={t(
          "modules.foundation.admin.journeys.steps.descriptionPlaceholder",
        )}
        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
      />
      <div
        data-testid="journey-step-tags"
        className="flex flex-wrap items-center gap-2"
      >
        <StepTagPicker
          kind="service"
          value={draft.service_id}
          onChange={(next) => setDraft((d) => ({ ...d, service_id: next }))}
        />
        <StepTagPicker
          kind="capability"
          value={draft.capability_id}
          onChange={(next) => setDraft((d) => ({ ...d, capability_id: next }))}
        />
        <StepTagPicker
          kind="vendor"
          value={draft.vendor_id}
          onChange={(next) => setDraft((d) => ({ ...d, vendor_id: next }))}
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onSave(draft)}
          disabled={!dirty}
          data-testid="journey-step-save"
          className="px-2 py-0.5 rounded bg-brand-brass text-brand-navy text-xs font-medium hover:bg-brand-brassDark disabled:opacity-50"
        >
          {t("modules.foundation.admin.journeys.steps.save")}
        </button>
        <button
          type="button"
          onClick={onDelete}
          data-testid="journey-step-delete"
          className="ms-auto px-2 py-0.5 rounded border border-red-300 text-red-700 text-xs hover:bg-red-50"
        >
          {t("modules.foundation.admin.journeys.steps.delete")}
        </button>
      </div>
    </div>
  );
}

/**
 * StepTagPicker -- inline dropdown for one of the 3 optional entity
 * tag columns on JourneyStep. Selecting the empty option clears
 * the FK (sends null). Reuses the same cache key as ParentSelect
 * for service + capability so the options list is shared across
 * drawers; vendor gets its own cache key.
 */
function StepTagPicker({
  kind,
  value,
  onChange,
}: {
  kind: TagKind;
  value: string | null;
  onChange: (next: string | null) => void;
}) {
  const { t } = useTranslation();
  const cacheKey =
    kind === "service" || kind === "capability"
      ? (["foundation-admin", kind, "parent-options"] as const)
      : (["foundation-admin", kind, "picker-options"] as const);
  const query = useQuery({
    queryKey: cacheKey,
    queryFn: () => api.get<TagOption[]>(TAG_ENDPOINTS[kind]),
    staleTime: 5 * 60_000,
  });
  return (
    <select
      value={value ?? ""}
      onChange={(e: ChangeEvent<HTMLSelectElement>) =>
        onChange(e.target.value === "" ? null : e.target.value)
      }
      data-testid={`journey-step-tag-${kind}`}
      data-tag-kind={kind}
      className="rounded border border-slate-300 px-1 py-0.5 text-xs"
      disabled={query.isLoading}
    >
      <option value="">
        {t(`modules.foundation.admin.journeys.steps.tagPlaceholder.${kind}`)}
      </option>
      {(query.data ?? []).map((p) => (
        <option key={p.id} value={p.id}>
          {p.name} ({p.code})
        </option>
      ))}
    </select>
  );
}

interface NewStepRowProps {
  nextStepNumber: number;
  onAdd: (draft: StepDraft) => void;
  adding: boolean;
}

function NewStepRow({ nextStepNumber, onAdd, adding }: NewStepRowProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<StepDraft>({
    step_number: nextStepNumber,
    name: "",
    description: null,
    service_id: null,
    capability_id: null,
    vendor_id: null,
  });
  useEffect(() => {
    setDraft((d) => ({ ...d, step_number: nextStepNumber }));
  }, [nextStepNumber]);

  return (
    <div
      data-testid="journey-step-add-row"
      className="rounded border border-dashed border-slate-300 p-2"
    >
      <div className="flex items-center gap-2 mb-1">
        <input
          type="number"
          min={1}
          value={draft.step_number}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setDraft((d) => ({ ...d, step_number: Number(e.target.value) }))
          }
          data-testid="journey-step-add-number"
          className="w-16 rounded border border-slate-300 px-1 py-0.5 text-sm tabular-nums"
        />
        <input
          type="text"
          value={draft.name}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setDraft((d) => ({ ...d, name: e.target.value }))
          }
          data-testid="journey-step-add-name"
          placeholder={t("modules.foundation.admin.journeys.steps.namePlaceholder")}
          className="flex-1 rounded border border-slate-300 px-2 py-0.5 text-sm"
        />
        <button
          type="button"
          onClick={() => {
            if (!draft.name.trim()) return;
            onAdd(draft);
            setDraft({
              step_number: draft.step_number + 1,
              name: "",
              description: null,
              service_id: null,
              capability_id: null,
              vendor_id: null,
            });
          }}
          disabled={adding || !draft.name.trim()}
          data-testid="journey-step-add"
          className="px-2 py-0.5 rounded bg-brand-brass text-brand-navy text-xs font-medium hover:bg-brand-brassDark disabled:opacity-50"
        >
          {t("modules.foundation.admin.journeys.steps.add")}
        </button>
      </div>
    </div>
  );
}
